import { useSelector } from 'react-redux';

import { Translate } from '@travel/translation';
import FeatureBadges from '@travel/traveler-core/components/ItemFeatures/FeatureBadges';
import { getFeatures, URL_KEYS } from '@travel/traveler-core/utils/jrdpHelper';
import { parseQueryString, stringify } from '@travel/utils';

import CategoryFeatures from 'components/CategoryFeatures';
import ImageGallery from 'components/ImageGallery';
import Link from 'core/universalRouter/Link';
import Header from './components/Header';
import RoomInfo from './components/RoomInfo';

import { URLSearchQuery } from 'Common-Types';
import paths from 'constants/paths';
import { HotelItem } from 'HotelList-Types';

import { getQueryString } from 'store/__router/selectors';

import styles from './hotelCard.module.scss';

type Props = {
  /** Object of provider data */
  provider: HotelItem;
  /** Flag to define whether the user device is smartphone or not */
  isSP?: boolean;
  /** Flag to define whether the video is showed as playable on thumbnail or not */
  isPlayableVideo?: boolean;
  /** String fo accommodation area path for navigation */
  accommodationAreaPath: string;
  /** String fo accommodations area paths for navigation */
  accommodationsPath: string;
  /** Search query from the URL*/
  query?: URLSearchQuery;
};

const HotelCard = (props: Props) => {
  const {
    provider,
    isSP = true,
    query,
    isPlayableVideo = false,
    accommodationAreaPath,
    accommodationsPath,
  } = props;

  const search = useSelector(getQueryString);
  const providerPopularFeatures = getFeatures(
    provider.providerFeatures,
    'Provider_Popular_Feature',
  );

  const composedSearchQuery = stringify({
    ...parseQueryString(search),
    [URL_KEYS.ACCOMMODATION_AREA]: accommodationAreaPath,
    [URL_KEYS.PLACE_OF_STAY]: accommodationsPath,
  });

  const planListUrl = `${paths.plansList.pathResolver(provider.id)}?${composedSearchQuery}`;
  const planDetailUrl = `${paths.planDetail.pathResolver(
    provider.id,
    provider.planId,
    provider.itemId,
  )}?${composedSearchQuery}`;

  const catchphrase = provider.catchphrase && (
    <p className={styles.catchphrase}>{provider.catchphrase}</p>
  );

  return (
    <article className={styles.container} data-testid="hotelCard-wrapper">
      {isSP ? (
        <>
          <Header
            providerName={provider.name}
            id={provider.id}
            reviewScore={provider.reviewScore}
            totalReviews={provider.totalReviews}
            classTitle={styles.hotelName}
            query={query}
          />
          <CategoryFeatures
            data-testid="hotelCard-specialCategory"
            features={provider.providerFeatures}
            className={styles.categoryBadges}
          />
          <ImageGallery
            media={provider.media}
            galleryType="slide"
            className={styles.mediaWrapper}
            mediaClassName={styles.media}
            data-testid="hotelCard-swiper"
            isPlayableVideo={isPlayableVideo}
            isGalleryDisable={true}
          />
          <FeatureBadges
            features={providerPopularFeatures}
            data-testid="popular-features"
            fill="shadyWhite"
            className={styles.popularFeatures}
          />
          {catchphrase}
        </>
      ) : (
        <div className={styles.flexBox}>
          <ImageGallery
            media={provider.media}
            className={styles.mediaWrapper}
            mediaClassName={styles.media}
            galleryType="slide"
            data-testid="hotelCard-swiper"
            isPlayableVideo={isPlayableVideo}
            isGalleryDisable={true}
          />
          <div className={styles.rightSide}>
            <Header
              providerName={provider.name}
              id={provider.id}
              reviewScore={provider.reviewScore}
              totalReviews={provider.totalReviews}
              classTitle={styles.hotelNamePC}
              query={query}
            />
            <CategoryFeatures
              data-testid="hotelCard-specialCategory"
              features={provider.providerFeatures}
              className={styles.categoryBadges}
            />
            {catchphrase}
            <FeatureBadges
              features={providerPopularFeatures}
              data-testid="popular-features"
              className={styles.popularFeaturesPC}
              fill="shadyWhite"
            />
          </div>
        </div>
      )}
      <RoomInfo
        roomName={provider.planName}
        price={provider.price}
        points={provider.points}
        roomSize={provider.roomSize}
        features={provider.itemFeatures}
        capacity={provider.capacity}
        navigateTo={planDetailUrl}
        availabilityCount={provider.availabilityCount}
        isSP={isSP}
        query={query}
        hasMeal={provider.hasMeal}
        departureStation={provider.departureStation?.name}
        destinationStation={provider.destinationStation?.name}
      />
      <div
        className={isSP ? styles.availablePlan : styles.availablePlanPC}
        data-testid="availablePlan"
      >
        <Link to={planListUrl} data-testid="hotelCard-morePlan-link">
          <Translate id="Hotel_List.List.See_More_Plan" data={{ plans: provider.allPlansCount }} />
        </Link>
      </div>
    </article>
  );
};

export default HotelCard;
